import { t } from "@lingui/macro"
import {
    FormControl,
    Stack,
    Typography,
    FormControlLabel,
    Switch,
    Box,
} from "@mui/material"
import { observer } from "mobx-react"
import { useCallback } from "react"

import { AutoCompleteTextField } from "src/components/AutoCompleteTextField"
import { KnowledgeItemStore } from "src/modals/knowledge-items/store"
import { useStore } from "src/store/lib/useStore"
import { config_ConfigDefinitionItem } from "src/api"

export const KnowledgeItemsAgentInstances = observer(() => {
    const store = useStore(KnowledgeItemStore)

    // Get all selected agent types that can have instances
    const agentTypesForInstances = store.getSelectedAgentTypesForInstances()

    const handleInstanceChange = useCallback(
        (agentName: string) => (_event: unknown, value: unknown) => {
            const instances = Array.isArray(value) ? value : []
            store.setSelectedInstancesForAgentType(agentName, instances)
        },
        [store],
    )

    const handleAgentTypeToggle = useCallback(
        (agentName: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
            const isEnabled = event.target.checked
            if (isEnabled) {
                // Enable this agent type by setting a special marker value
                store.setSelectedInstancesForAgentType(agentName, [
                    { id: -1, label: "all-instances" },
                ])
            } else {
                // When disabled, clear selection but don't prevent manual selection
                store.setSelectedInstancesForAgentType(agentName, [])
            }
        },
        [store],
    )

    const isAgentTypeEnabledForAll = useCallback(
        (agentName: string): boolean => {
            const selectedInstances =
                store.getSelectedInstancesForAgentType(agentName)
            return selectedInstances.some((instance) => instance.id === -1)
        },
        [store],
    )

    const getManuallySelectedInstances = useCallback(
        (agentName: string) => {
            const selectedInstances =
                store.getSelectedInstancesForAgentType(agentName)
            return selectedInstances.filter((instance) => instance.id !== -1)
        },
        [store],
    )

    // Check if we have any existing instance configurations even without agent types selected
    const hasExistingInstanceConfigurations =
        store.hasExistingInstanceConfigurations()

    // Don't render anything if no agent types are selected AND no existing instance configurations
    if (
        agentTypesForInstances.length === 0 &&
        !hasExistingInstanceConfigurations
    ) {
        return null
    }

    // Get all agent types that have existing instance configurations (for legacy items)
    const getAllAgentTypesWithInstanceConfigurations = () => {
        const agentInstances = store.formFields.get("agent_instances") ?? {}
        const agentTypesWithConfigs: config_ConfigDefinitionItem[] = []

        // Add selected agent types
        agentTypesWithConfigs.push(...agentTypesForInstances)

        // Add agent types that have existing instance configurations but aren't selected
        Object.keys(agentInstances).forEach((agentName) => {
            const agentDefinition = store.agentDefinitions.find(
                (def) => def.name === agentName,
            )
            if (
                agentDefinition != null &&
                !agentTypesWithConfigs.some(
                    (existing: config_ConfigDefinitionItem) =>
                        existing.name === agentName,
                )
            ) {
                agentTypesWithConfigs.push(agentDefinition)
            }
        })

        return agentTypesWithConfigs
    }

    const allAgentTypesToShow = getAllAgentTypesWithInstanceConfigurations()

    return (
        <Stack spacing={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t`knowledge-item-modal.agent-configuration.title`}
            </Typography>
            {allAgentTypesToShow.map(
                (agentDefinition: config_ConfigDefinitionItem) => {
                    const agentName = agentDefinition.name
                    if (agentName == null || agentName.length === 0) return null

                    const requiresInstances = Boolean(
                        (agentDefinition.metadata as Record<string, unknown>)
                            ?.agent_instance_required,
                    )
                    const instances = store.getInstancesForAgentType(agentName)
                    const selectedInstances =
                        store.getSelectedInstancesForAgentType(agentName)

                    if (requiresInstances) {
                        // Show instance dropdown for agents that require specific instances
                        return (
                            <FormControl
                                key={agentName}
                                sx={{ breakInside: "avoid" }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{ mb: 1, fontWeight: 500 }}
                                >
                                    {t({
                                        id: "knowledge-item-modal.agent-configuration.select-specific-instances",
                                        values: { agentName },
                                    })}
                                </Typography>
                                <AutoCompleteTextField
                                    multiple
                                    disableCloseOnSelect
                                    options={instances}
                                    value={selectedInstances}
                                    placeholder={t({
                                        id: "knowledge-item-modal.agent-configuration.select-instances-placeholder",
                                        values: { agentName },
                                    })}
                                    onChange={handleInstanceChange(agentName)}
                                    loading={instances.length === 0}
                                />
                            </FormControl>
                        )
                    } else {
                        // Show toggle + optional instance selection for agents that don't require specific instances
                        const isEnabledForAll =
                            isAgentTypeEnabledForAll(agentName)
                        const manuallySelected =
                            getManuallySelectedInstances(agentName)

                        return (
                            <Box key={agentName} sx={{ breakInside: "avoid" }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isEnabledForAll}
                                            onChange={handleAgentTypeToggle(
                                                agentName,
                                            )}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 500 }}
                                            >
                                                {agentName}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {t`knowledge-item-modal.agent-configuration.make-available-all-instances`}
                                            </Typography>
                                        </Box>
                                    }
                                />
                                {!isEnabledForAll && (
                                    <Box sx={{ mt: 1, ml: 4 }}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ mb: 1, display: "block" }}
                                        >
                                            {t`knowledge-item-modal.agent-configuration.or-select-specific-instances`}
                                        </Typography>
                                        <AutoCompleteTextField
                                            multiple
                                            disableCloseOnSelect
                                            options={instances}
                                            value={manuallySelected}
                                            placeholder={t({
                                                id: "knowledge-item-modal.agent-configuration.select-specific-instances-placeholder",
                                                values: { agentName },
                                            })}
                                            onChange={handleInstanceChange(
                                                agentName,
                                            )}
                                            loading={instances.length === 0}
                                            size="small"
                                        />
                                    </Box>
                                )}
                            </Box>
                        )
                    }
                },
            )}
        </Stack>
    )
})
